import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { PageToolbarComponent } from '../../components/page-toolbar/page-toolbar.component';
import { PostService } from '../../services/post.service';
import { AuthService } from '../../services/auth.service';
import { PostDto } from '../../dto/post.dto';
import { ProcessResult } from '../../components/common-dto/query.dto';

@Component({
  selector: 'app-post-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    MultiSelectModule,
    ToastModule,
    PageToolbarComponent,
  ],
  providers: [MessageService],
  template: `
    <pb-page-toolbar
      [header]="isNew ? 'Create a Post' : 'Edit Post'"
      [supportsEdit]="false"
      [supportsAdd]="false"
      [supportsDelete]="!isNew"
      [isEditing]="true"
      (onSave)="savePost()"
      (onCancel)="cancel()"
      (onDelete)="deletePost()"
    ></pb-page-toolbar>

    <div class="post-edit-container">
      <div class="form-section">
        <div class="form-field">
          <label for="content">Content <span class="required">*</span></label>
          <textarea
            id="content"
            pTextarea
            [(ngModel)]="post.content"
            [rows]="1"
            [autoResize]="true"
            placeholder="Title"
            class="full-width"
          ></textarea>
        </div>

        <div class="form-field">
          <label for="categories">Categories</label>
          <p-multiSelect
            id="categories"
            [(ngModel)]="post.categories"
            [options]="categoryOptions"
            placeholder="Select categories"
            [displaySelectedLabel]="true"
            [showClear]="true"
            class="full-width"
          ></p-multiSelect>
        </div>

        <div class="form-field">
          <label for="videoUpload">Video Upload</label>
          <div class="file-upload-section">
            <input
              type="file"
              id="videoUpload"
              #fileInput
              accept="video/*"
              (change)="onFileSelected($event)"
              style="display: none"
            />
            <div class="file-upload-controls">
              <button
                pButton
                type="button"
                label="Choose Video"
                icon="pi pi-upload"
                (click)="fileInput.click()"
                class="p-button-outlined"
              ></button>
              <span *ngIf="selectedFile" class="file-name">
                {{ selectedFile.name }}
              </span>
              <button
                *ngIf="selectedFile"
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-rounded"
                (click)="clearFile()"
              ></button>
            </div>
            <div *ngIf="post.mediaUrl && !selectedFile" class="current-media">
              <p>Current media: {{ post.mediaUrl }}</p>
              <button
                pButton
                type="button"
                label="Remove Media"
                icon="pi pi-trash"
                class="p-button-text p-button-danger"
                (click)="removeMedia()"
              ></button>
            </div>
            <small class="file-hint">
              Supported formats: MP4, MOV, AVI, WebM (Max 100MB)
            </small>
          </div>
        </div>

        <div class="form-actions">
          <button
            pButton
            type="button"
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            (click)="cancel()"
          ></button>
          <button
            pButton
            type="button"
            label="{{ isNew ? 'Create Post' : 'Update Post' }}"
            icon="pi pi-check"
            (click)="savePost()"
            [disabled]="!post.content || saving"
          ></button>
        </div>
      </div>
    </div>

    <p-toast></p-toast>
  `,
  styles: [
    `
      .post-edit-container {
        padding: 1.5rem;
        max-width: 800px;
        margin: 0 auto;
      }

      .form-section {
        background: #ffffff;
        border-radius: 8px;
        padding: 2rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .form-field {
        margin-bottom: 2rem;
      }

      .form-field label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 600;
        color: #333333;
      }

      .required {
        color: #e74c3c;
      }

      .full-width {
        width: 100%;
      }

      .file-upload-section {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .file-upload-controls {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .file-name {
        flex: 1;
        color: #666666;
        font-size: 0.9rem;
      }

      .current-media {
        padding: 1rem;
        background: #f5f5f5;
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .current-media p {
        margin: 0;
        color: #666666;
      }

      .file-hint {
        color: #999999;
        font-size: 0.85rem;
      }

      .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 2rem;
        padding-top: 2rem;
        border-top: 1px solid #e5e5e5;
      }

      :host ::ng-deep {
        .p-button {
          min-width: 120px;
        }

        .p-multiselect {
          width: 100%;
        }

        .p-textarea {
          width: 100%;
        }
      }
    `,
  ],
})
export class PostEditComponent implements OnInit {
  post: PostDto = {
    content: '',
    categories: [],
  };
  isNew = true;
  postId: string | null = null;
  selectedFile: File | null = null;
  saving = false;

  categoryOptions = [
    { label: 'IT', value: 'IT' },
    { label: 'Construction', value: 'Construction' },
    { label: 'Medical', value: 'Medical' },
    { label: 'Finance', value: 'Finance' },
    { label: 'Education', value: 'Education' },
    { label: 'Retail', value: 'Retail' },
    { label: 'Manufacturing', value: 'Manufacturing' },
    { label: 'Agriculture', value: 'Agriculture' },
  ];

  constructor(
    private postService: PostService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.postId = this.route.snapshot.params['id'];
    this.isNew = !this.postId || this.postId === 'new';

    if (!this.isNew && this.postId) {
      this.loadPost();
    } else {
      // Set default tenant ID for new posts
      const tenant = this.authService.getCurrentTenant();
      if (tenant) {
        this.post.tenantId = tenant.id;
      }
    }
  }

  loadPost() {
    if (!this.postId) return;

    this.postService.getPost(this.postId).subscribe({
      next: (post) => {
        this.post = post;
      },
      error: (error) => {
        console.error('Error loading post:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load post',
        });
      },
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      if (!file.type.startsWith('video/')) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid File',
          detail: 'Please select a video file',
        });
        return;
      }

      // Validate file size (100MB max)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (file.size > maxSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: 'File size must be less than 100MB',
        });
        return;
      }

      this.selectedFile = file;
      this.post.mediaType = 'video';
    }
  }

  clearFile() {
    this.selectedFile = null;
    const fileInput = document.getElementById(
      'videoUpload'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  removeMedia() {
    this.post.mediaUrl = undefined;
    this.post.mediaType = undefined;
  }

  savePost() {
    if (!this.post.content || this.post.content.trim() === '') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Content is required',
      });
      return;
    }

    this.saving = true;

    // First, save the post
    const saveObservable = this.isNew
      ? this.postService.createPost(this.post)
      : this.postService.updatePost(this.postId!, this.post);

    saveObservable.subscribe({
      next: (result: ProcessResult) => {
        // If there's a file to upload, upload it after creating/updating the post
        if (this.selectedFile && result.id) {
          this.uploadMedia(result.id);
        } else {
          this.saving = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.isNew
              ? 'Post created successfully'
              : 'Post updated successfully',
          });
          this.router.navigate(['/feed']);
        }
      },
      error: (error) => {
        this.saving = false;
        console.error('Error saving post:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.isNew
            ? 'Failed to create post'
            : 'Failed to update post',
        });
      },
    });
  }

  uploadMedia(postId: string) {
    if (!this.selectedFile) return;

    this.postService.uploadPostMedia(postId, this.selectedFile).subscribe({
      next: (result: ProcessResult) => {
        this.saving = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Post and media uploaded successfully',
        });
        this.router.navigate(['/feed']);
      },
      error: (error) => {
        this.saving = false;
        console.error('Error uploading media:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Post saved but media upload failed',
        });
        // Still navigate to feed even if media upload fails
        this.router.navigate(['/feed']);
      },
    });
  }

  deletePost() {
    if (!this.postId) return;

    this.postService.deletePost(this.postId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Post deleted successfully',
        });
        this.router.navigate(['/feed']);
      },
      error: (error) => {
        console.error('Error deleting post:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete post',
        });
      },
    });
  }

  cancel() {
    this.router.navigate(['/feed']);
  }
}
